import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import { useLoadLeagueData } from '../../../../app/dataLoaderHooks';
import { League } from '../../../../sdk/model';
import { Typography, Box, Tabs, Tab, Paper } from '@mui/material';
import { PowerRankingRowData, ChartDataPoint, COLORS, rank, a11yProps } from './types';
import { TabPanel } from './TabPanel';
import { OverviewTab } from './OverviewTab';
import { PerformanceTab } from './PerformanceTab';
import { CoachingTab } from './CoachingTab';
import { AnalyticsTab } from './AnalyticsTab';

const WIN_PCTG_FACTOR = 5;
const MEDIAN_FACTOR = 5
const PTS_FACTOR = 8;
const OPTIMAL_PTS_FACTOR = 2
const COACHING_FACTOR = 2

function PowerRankings() {
  const {leagueId} = useParams();
  
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const user = useAppSelector((state) => state.user);
  
  const league : League|undefined = useLoadLeagueData({leagueId: leagueId, userId: user.id, loadBasicAuctionData: false, 
    loadFullAuctionData: false, loadContractData: false, loadRosterData: false,
    loadBasicDraftData: false, loadFullDraftData: false, loadRosterFor: [], 
    loadWaivedContracts: false, loadScheduleData: false,
    setLoadComplete: setLoadComplete, isLoadComplete: isLoadComplete,
    setIsException: setIsException, isException: isException});
  
  if(isException) {
    return <Typography color="red">There was an error loading the league</Typography>
  } else if(!isLoadComplete) {
    return <Typography color="white">Loading league data..</Typography>
  }
  
  const data: PowerRankingRowData[] = league?.teams.map(team => {
    const currentSeason = team.currentSeason!;
    const gamesPlayed = currentSeason.wins! + currentSeason.losses! + currentSeason.ties!;
    const wins = currentSeason.wins! + currentSeason.ties! / 2.0;
    const winPctg = gamesPlayed ? wins / gamesPlayed : undefined;
    const winsAgainstMedian = currentSeason.winsAgainstMedian! + currentSeason.tiesAgainstMedian! / 2.0
    const winPctgAgainstMedian = gamesPlayed ? winsAgainstMedian / gamesPlayed : undefined;
    const optimalPointsDiff = currentSeason.optimalPointsScored! - currentSeason.pointsScored!;
    const optimalPointsDiffPctg = currentSeason.optimalPointsScored ? optimalPointsDiff / currentSeason.optimalPointsScored : undefined
    return {
      teamName: team.name!,
      winPctg: winPctg,
      winPctgRank: 0,
      winPctgAgainstMedian: winPctgAgainstMedian,
      winPctgAgainstMedianRank: 0,
      totalPoints: currentSeason.pointsScored!,
      totalPointsRank: 0,
      totalOptimalPoints: currentSeason.optimalPointsScored!,
      totalOptimalPointsRank: 0,
      optimalPointsDiff: optimalPointsDiff,
      optimalPointsDiffPctg: optimalPointsDiffPctg,
      coachRank: 0,
      powerPoints: 0,
      powerRank: 0,
    }
  })!;
  
  rank(data, "winPctg", "winPctgRank", true);
  rank(data, "winPctgAgainstMedian", "winPctgAgainstMedianRank", true);
  rank(data, "totalPoints", "totalPointsRank", true);
  rank(data, "totalOptimalPoints", "totalOptimalPointsRank", true);
  rank(data, "optimalPointsDiffPctg", "coachRank", false);
  
  const numberOfTeams = league?.teams.length!;
  
  function calculatePowerPoints(rank, factor) {
    return (numberOfTeams - rank + 1) * factor;
  }
  
  data.forEach(team => {
    team.powerPoints = calculatePowerPoints(team.winPctgRank, WIN_PCTG_FACTOR) + calculatePowerPoints(team.winPctgAgainstMedianRank, MEDIAN_FACTOR) + calculatePowerPoints(team.totalPointsRank, PTS_FACTOR) + calculatePowerPoints(team.totalOptimalPointsRank, OPTIMAL_PTS_FACTOR) + calculatePowerPoints(team.coachRank, COACHING_FACTOR);
  })
  
  rank(data, "powerPoints", "powerRank", true);

  // Prepare chart data
  const chartData: ChartDataPoint[] = data.map((team, index) => {
    const teamSeason = league?.teams.find(t => t.name === team.teamName)?.currentSeason;
    const teamData = league?.teams.find(t => t.name === team.teamName);
    return {
      name: team.teamName.length > 8 ? team.teamName.substring(0, 8) + '...' : team.teamName,
      shortName: team.teamName.length > 6 ? team.teamName.substring(0, 6) : team.teamName,
      fullName: team.teamName,
      powerRank: team.powerRank,
      winPct: team.winPctg ? Math.round(team.winPctg * 1000) / 10 : 0,
      totalPoints: Math.round(team.totalPoints),
      coachingEfficiency: team.optimalPointsDiffPctg ? 100 - Math.round(team.optimalPointsDiffPctg * 1000) / 10 : 0,
      optimalPoints: Math.round(team.totalOptimalPoints),
      actualPoints: Math.round(team.totalPoints),
      pointsLeft: Math.round(team.optimalPointsDiff),
      wins: teamSeason?.wins || 0,
      losses: teamSeason?.losses || 0,
      ties: teamSeason?.ties || 0,
      winPctgRank: team.winPctgRank,
      pointsRank: team.totalPointsRank,
      coachRank: team.coachRank,
      color: COLORS[index % COLORS.length],
      teamData: {
        picture: teamData?.picture,
        fullName: team.teamName,
        shortName: team.teamName.length > 6 ? team.teamName.substring(0, 6) : team.teamName
      }
    };
  }).sort((a, b) => a.powerRank - b.powerRank);
    
  return (
    <Box>
      <Typography variant="h4" sx={{ flexGrow: 1, mb: 3 }}>
        League Power Rankings
      </Typography>
      
      <Paper elevation={2}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="power rankings tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab label="Performance" {...a11yProps(1)} />
          <Tab label="Coaching" {...a11yProps(2)} />
          <Tab label="Analytics" {...a11yProps(3)} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <OverviewTab data={data} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <PerformanceTab data={data} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <CoachingTab data={data} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <AnalyticsTab league={league!} chartData={chartData} />
        </TabPanel>
      </Paper>
    </Box>
  );
}

export default PowerRankings;
